import { useEffect, useMemo, useState } from "react";

import { createFirebaseUserService } from "@/core/services/factory";
import type { PreferenceStat } from "@/core/services/types/firebase-user.types";
import { useTranslations } from "@/lib/useTranslations";

function PreferenceTable({
  title,
  rows,
  emptyLabel,
  countHeader,
}: {
  title: string;
  rows: PreferenceStat[];
  emptyLabel: string;
  countHeader: string;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-black/10 bg-white shadow-sm">
      <div className="border-b border-black/10 bg-yellow-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-zinc-900">{title}</h2>
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-neutral-500">
          {emptyLabel}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-black/10 bg-zinc-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3 font-medium">{title}</th>
                <th className="px-4 py-3 text-right font-medium">
                  {countHeader}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr
                  key={row.id}
                  className={`border-b border-black/5 last:border-0 ${
                    index % 2 === 1 ? "bg-zinc-50/80" : "bg-white"
                  }`}
                >
                  <td className="px-4 py-2.5 text-whart-text">{row.label}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">
                    {row.count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function UsersPreferencesPanel() {
  const t = useTranslations("users.preferences");
  const usersService = useMemo(() => createFirebaseUserService(), []);

  const [artForms, setArtForms] = useState<PreferenceStat[]>([]);
  const [artisticPeriods, setArtisticPeriods] = useState<PreferenceStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    void usersService
      .listPreferenceStats()
      .then((result) => {
        if (cancelled) return;
        setArtForms(result.artForms);
        setArtisticPeriods(result.artisticPeriods);
      })
      .catch(() => {
        if (cancelled) return;
        setError(t("loadError"));
        setArtForms([]);
        setArtisticPeriods([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [t, usersService]);

  if (loading) {
    return (
      <div className="rounded-xl border border-black/10 bg-white px-4 py-10 text-center text-sm text-neutral-500 shadow-sm">
        {t("loading")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <PreferenceTable
          title={t("artFormsTitle")}
          rows={artForms}
          emptyLabel={t("empty")}
          countHeader={t("countHeader")}
        />
        <PreferenceTable
          title={t("artisticPeriodsTitle")}
          rows={artisticPeriods}
          emptyLabel={t("empty")}
          countHeader={t("countHeader")}
        />
      </div>
    </div>
  );
}
